'use strict'
import { useMemo, useState } from 'react'
import {
  CircularProgress,
  IconButton,
  TextField,
  Tooltip
} from '@material-ui/core'
import VisibilityIcon from '@material-ui/icons/Visibility'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { DFToggle } from '../../df'
import { routeDisplayName } from '../routeDisplayName'
import { PropertyDetailDrawer } from '../../PropertyDetail'

const LEGACY_FREQUENCY_OPTIONS = [
  { value: '', label: 'Unspecified' },
  { value: 'Daily', label: 'Daily' },
  { value: 'Every Other Day (M-W-F)', label: 'Every Other Day (M-W-F)' },
  { value: 'Twice a Week', label: 'Twice a Week' },
  { value: 'Weekly', label: 'Weekly' },
  { value: 'Bi-Weekly', label: 'Bi-Weekly' },
  { value: 'Monthly', label: 'Monthly' },
  { value: 'Quarterly', label: 'Quarterly' },
  { value: 'Semi-Annually', label: 'Semi-Annually' },
  { value: 'Annually', label: 'Annually' },
  { value: 'On Demand', label: 'On Demand' }
]

const DAY_OPTIONS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']

const WEEK_TYPE_OPTIONS = [
  { value: '', label: 'Any week' },
  { value: 'A', label: 'Week A' },
  { value: 'B', label: 'Week B' }
]

const sortDays = (days = []) => {
  return [...new Set(days.filter(Boolean))]
    .sort((a, b) => DAY_OPTIONS.indexOf(a) - DAY_OPTIONS.indexOf(b))
}

const optionByValue = (options, value) => {
  return options.find(option => String(option.value || '') === String(value || '')) || null
}

const routeLabel = (item = {}) => routeDisplayName(item)

const routeId = (item) => item?._id || item?.id || item?.routeId || item?.crewId || ''

const fieldStyles = {
  minWidth: 180
}

const renderDfInput = (params, placeholder) => (
  <TextField
    {...params}
    variant="outlined"
    placeholder={placeholder}
    InputProps={{
      ...params.InputProps,
      style: {
        ...params.InputProps.style,
        background: 'var(--df-surface)',
        color: 'var(--df-ink)',
        fontSize: 12,
        fontFamily: 'inherit'
      }
    }}
  />
)

/* ── Inline SVG icons ───────────────────────────────────────────── */
const LightningSvg = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
    <path d="M13 2L3 14h9l-1 10 10-12h-9l1-10z" />
  </svg>
)
const SyncSvg = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
)
const CollapseSvg = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="18 15 12 9 6 15" />
  </svg>
)

/* ── Shared micro-label ─────────────────────────────────────────── */
const FieldLabel = ({ children }) => (
  <div style={{
    fontSize: 10, fontWeight: 700, color: 'var(--df-ink-4)',
    textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4
  }}>{children}</div>
)

/* ── Small DF-styled button ─────────────────────────────────────── */
const DFBtn = ({ variant = 'ghost', disabled, onClick, children }) => {
  const isPrimary = variant === 'primary'
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600,
        fontFamily: 'inherit', cursor: disabled ? 'not-allowed' : 'pointer',
        border: isPrimary ? 'none' : '1px solid var(--df-line-2)',
        background: isPrimary ? 'var(--df-primary)' : 'var(--df-surface)',
        color: isPrimary ? '#fff' : 'var(--df-primary)',
        opacity: disabled ? 0.5 : 1,
        transition: 'background .12s, opacity .12s',
      }}
    >{children}</button>
  )
}

export default function PropertyRowDetail ({
  row,
  crews,
  routes,
  isEditable,
  isLiveMode,
  isDirty,
  isSaving,
  isMoving,
  isSuggesting,
  isResyncing,
  moveTarget,
  url,
  allRows,
  onUpdateField,
  onUpdateFrequency,
  onMoveTargetChange,
  onMove,
  onSuggest,
  onSave,
  onResync,
  onCollapse
}) {
  const [quickDetailsOpen, setQuickDetailsOpen] = useState(false)
  const currentAssignment = row.assignedRouteId || row.assignedCrewId
  const canMove = isEditable && !isLiveMode && moveTarget && moveTarget !== currentAssignment && !isMoving
  const moveOptions = (routes && routes.length > 0) ? routes : crews
  const lockOptions = (routes && routes.length > 0) ? routes : crews
  const selectedFrequency = optionByValue(LEGACY_FREQUENCY_OPTIONS, row.frequency || '') || LEGACY_FREQUENCY_OPTIONS[0]
  const selectedDay = row.preferredDay || null
  const selectedWeekType = optionByValue(WEEK_TYPE_OPTIONS, row.weekType || '') || WEEK_TYPE_OPTIONS[0]
  const selectedLockId = row.lockedRouteId || row.lockedCrewId || ''
  const selectedLockOption = selectedLockId
    ? lockOptions.find(item => String(routeId(item)) === String(selectedLockId)) || null
    : { _id: '', name: 'Unlocked' }
  const selectedMoveOption = moveTarget
    ? moveOptions.find(item => String(routeId(item)) === String(moveTarget)) || null
    : null
  const detailProperty = useMemo(() => ({
    ...row,
    name: row.name || row.propertyName,
    address1: row.address1 || row.address,
    propertyId: row.propertyId || row.aspirePropertyId,
    primaryCrewLeader: row.primaryCrewLeader || (row.assignedCrewName ? { name: row.assignedCrewName } : null),
    scheduledDays: row.scheduledDays || (row.preferredDay ? [row.preferredDay] : [])
  }), [row])

  return (
    <div className="prop-detail">
      {/* ── Frequency + schedule row ────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 16, marginBottom: 14 }}>
        <div>
          <FieldLabel>Frequency</FieldLabel>
          <Autocomplete
            size="small"
            disableClearable
            options={LEGACY_FREQUENCY_OPTIONS}
            value={selectedFrequency}
            getOptionLabel={(option) => option?.label || ''}
            getOptionSelected={(option, value) => String(option?.value || '') === String(value?.value || '')}
            onChange={(event, option) => onUpdateFrequency(row.aspirePropertyId, option?.value || '')}
            style={fieldStyles}
            disabled={!isEditable}
            renderInput={(params) => renderDfInput(params, 'Frequency')}
          />
        </div>

        {(row.frequency === 'Weekly' || row.frequency === 'Bi-Weekly') && (
          <div>
            <FieldLabel>Day</FieldLabel>
            <Autocomplete
              size="small"
              options={DAY_OPTIONS}
              value={selectedDay}
              getOptionLabel={(option) => option || ''}
              onChange={(event, day) => onUpdateField(row.aspirePropertyId, 'preferredDay', day || '')}
              style={{ minWidth: 140 }}
              disabled={!isEditable}
              renderInput={(params) => renderDfInput(params, 'No day')}
            />
          </div>
        )}

        {row.frequency === 'Twice a Week' && (
          <div>
            <FieldLabel>Days</FieldLabel>
            <Autocomplete
              multiple
              size="small"
              options={DAY_OPTIONS}
              value={sortDays(row.preferredDays || [])}
              getOptionLabel={(option) => option || ''}
              onChange={(event, incoming) => {
                const next = sortDays(incoming).slice(0, 2)
                onUpdateField(row.aspirePropertyId, 'preferredDays', next)
                if (!row.preferredDay && next[0]) {
                  onUpdateField(row.aspirePropertyId, 'preferredDay', next[0])
                }
              }}
              style={{ minWidth: 220 }}
              disabled={!isEditable}
              renderInput={(params) => renderDfInput(params, 'No days')}
            />
          </div>
        )}

        {row.frequency === 'Bi-Weekly' && (
          <div>
            <FieldLabel>Week Type</FieldLabel>
            <Autocomplete
              size="small"
              disableClearable
              options={WEEK_TYPE_OPTIONS}
              value={selectedWeekType}
              getOptionLabel={(option) => option?.label || ''}
              getOptionSelected={(option, value) => String(option?.value || '') === String(value?.value || '')}
              onChange={(event, option) => onUpdateField(row.aspirePropertyId, 'weekType', option?.value || '')}
              style={{ minWidth: 140 }}
              disabled={!isEditable}
              renderInput={(params) => renderDfInput(params, 'Week')}
            />
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginLeft: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FieldLabel>VIP</FieldLabel>
            <DFToggle
              checked={!!row.isVip}
              onChange={(val) => onUpdateField(row.aspirePropertyId, 'isVip', val)}
              disabled={!isEditable}
            />
          </div>
        </div>

        <div>
          <FieldLabel>Prescribed hrs</FieldLabel>
          <TextField
            type="number"
            size="small"
            variant="outlined"
            value={row.prescribedManHours ?? ''}
            onChange={(event) => onUpdateField(row.aspirePropertyId, 'prescribedManHours', event.target.value === '' ? null : Number(event.target.value))}
            placeholder="None"
            inputProps={{ min: 0, step: 0.25 }}
            disabled={!isEditable}
            style={{ width: 130 }}
          />
        </div>
      </div>

      {/* ── Lock + Move row ─────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'flex-end', flexWrap: 'wrap', gap: 16, marginBottom: 14 }}>
        <div>
          <FieldLabel>Lock to route</FieldLabel>
          <Autocomplete
            size="small"
            options={[{ _id: '', name: 'Unlocked' }, ...lockOptions]}
            value={selectedLockOption}
            getOptionLabel={(option) => routeLabel(option)}
            getOptionSelected={(option, value) => String(routeId(option)) === String(routeId(value))}
            onChange={(event, option) => {
              const val = routeId(option)
              const isRoute = (routes || []).some(r => String(routeId(r)) === String(val))
              if (isRoute) {
                onUpdateField(row.aspirePropertyId, 'lockedRouteId', val)
                const route = (routes || []).find(r => String(routeId(r)) === String(val))
                if (route?.crewId) onUpdateField(row.aspirePropertyId, 'lockedCrewId', route.crewId)
              } else {
                onUpdateField(row.aspirePropertyId, 'lockedRouteId', '')
                onUpdateField(row.aspirePropertyId, 'lockedCrewId', val)
              }
            }}
            style={{ minWidth: 220 }}
            disabled={!isEditable}
            renderInput={(params) => renderDfInput(params, 'Unlocked')}
          />
        </div>

        <div>
          <FieldLabel>Move to route</FieldLabel>
          <Autocomplete
            size="small"
            options={moveOptions}
            value={selectedMoveOption}
            getOptionLabel={(option) => routeLabel(option)}
            getOptionSelected={(option, value) => String(routeId(option)) === String(routeId(value))}
            onChange={(event, option) => onMoveTargetChange(row.aspirePropertyId, routeId(option))}
            style={{ minWidth: 420 }}
            disabled={!isEditable || isLiveMode || isMoving}
            renderInput={(params) => renderDfInput(params, isLiveMode ? 'Scenario required' : 'Select route')}
          />
        </div>

        <DFBtn variant="ghost" disabled={!canMove} onClick={() => onMove(row)}>
          {isLiveMode ? 'Scenario required' : isMoving ? 'Moving…' : 'Move'}
        </DFBtn>
        <DFBtn variant="ghost" disabled={!isEditable || isLiveMode || isSuggesting} onClick={() => onSuggest(row)}>
          <LightningSvg />
          {isSuggesting ? 'Suggesting…' : 'Suggest Route'}
        </DFBtn>
      </div>

      {/* ── Resync row ──────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <span style={{ fontSize: 11, color: 'var(--df-ink-4)' }}>
          Last synced: {row.lastSyncedAt ? new Date(row.lastSyncedAt).toLocaleString() : 'Never'}
        </span>
        <DFBtn variant="ghost" disabled={isResyncing} onClick={() => onResync(row)}>
          {isResyncing ? <CircularProgress size={12} style={{ color: 'var(--df-primary)' }} /> : <SyncSvg />}
          {isResyncing ? 'Syncing…' : 'Resync from Aspire'}
        </DFBtn>
        <Tooltip title="View details">
          <IconButton size="small" onClick={() => setQuickDetailsOpen(true)} aria-label="View details">
            <VisibilityIcon style={{ fontSize: 18, color: 'var(--df-primary)' }} />
          </IconButton>
        </Tooltip>
      </div>

      <PropertyDetailDrawer
        open={quickDetailsOpen}
        onClose={() => setQuickDetailsOpen(false)}
        property={detailProperty}
        url={url}
        allRows={allRows}
      />

      {/* ── Actions ─────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', justifyContent: 'flex-end', gap: 8,
        paddingTop: 12, borderTop: '1px solid var(--df-line)'
      }}>
        <DFBtn variant="ghost" onClick={onCollapse}>
          <CollapseSvg /> Collapse
        </DFBtn>
        <DFBtn
          variant="primary"
          disabled={!isEditable || !isDirty || isSaving}
          onClick={() => onSave(row)}
        >
          {isSaving
            ? <><CircularProgress size={12} style={{ color: '#fff' }} /> Saving…</>
            : isLiveMode ? 'Save to Live' : 'Save Changes'}
        </DFBtn>
      </div>
    </div>
  )
}
